import axios from 'axios';
import { calculateMinutesSince } from '../util';
import { tenXService } from '../../../../../services/TenXService';

// Shape mirrors the 10x API's PrimaryImageMetadata / SecondaryImageMetadata models
// (10x-command-and-control-api/src/modules/telemetry/telemetry_models.py). Only
// device_sn / timestamp / uuid / camera_id are guaranteed; everything else is nullable.
export interface ImageMetadata {
  device_sn: string;
  timestamp: string;
  uuid: string;
  camera_id: string;
  camera_classification?: string | null;
  image_s3_key?: string | null;
  width?: number | null;
  height?: number | null;
  format?: string | null;
  size_bytes?: number | null;
  checksum_sha256?: string | null;
  capture_duration_ms?: number | null;
  jpeg_quality?: number | null;
  exposure_auto?: number | null;
  exposure_absolute?: number | null;
  exposure_auto_priority?: number | null;
  brightness?: number | null;
  contrast?: number | null;
  saturation?: number | null;
  hue?: number | null;
  white_balance_automatic?: number | null;
  white_balance_temperature?: number | null;
  gamma?: number | null;
  gain?: number | null;
  focus_auto?: number | null;
  focus_absolute?: number | null;
  sharpness?: number | null;
  backlight_compensation?: number | null;
  power_line_frequency?: number | null;
  created_at?: string | null;
}

export interface DisplayFields {
  timestamp: string;
  camera_id: string;
  size_bytes: number | null;
  capture_duration_ms: number | null;
  jpeg_quality: number | null;
  lastUpdatedMinutes: number;
}

// Image and metadata are fetched independently; either can be missing (null) with the
// reason in the matching *Error field.
export interface FetchDeviceImageResult {
  imageObjectUrl: string | null;
  displayFields: DisplayFields | null;
  metadata: ImageMetadata | null;
  imageError: string | null;
  metadataError: string | null;
}

// Turn a failed request into a message an operator can act on. A 404 is an expected
// "no data" state, a missing response means the API itself was unreachable or timed out.
const describeFetchError = (err: unknown, what: string): string => {
  if (axios.isAxiosError(err)) {
    if (err.response?.status === 404) return `No ${what} found for this camera.`;
    if (!err.response) return `Could not load ${what}: the API did not respond.`;
    return `Could not load ${what} (HTTP ${err.response.status}).`;
  }
  return err instanceof Error ? err.message : `Could not load ${what}.`;
};

// Never rejects for a failed fetch, so a missing image or metadata record can't take
// down the rest of the device view.
export const fetchDeviceImageAndMetadata = async (
  deviceId: string,
  secondary = false,
): Promise<FetchDeviceImageResult> => {
  const [imageResult, metadataResult] = await Promise.allSettled([
    tenXService.getDeviceImage(deviceId, secondary),
    tenXService.getDeviceImageMetadata(deviceId, secondary),
  ]);

  let imageObjectUrl: string | null = null;
  let imageError: string | null = null;
  if (imageResult.status === 'fulfilled') {
    imageObjectUrl = URL.createObjectURL(imageResult.value as Blob);
  } else {
    imageError = describeFetchError(imageResult.reason, 'image');
  }

  let metadata: ImageMetadata | null = null;
  let displayFields: DisplayFields | null = null;
  let metadataError: string | null = null;
  if (metadataResult.status === 'fulfilled') {
    metadata = metadataResult.value as ImageMetadata;
    displayFields = {
      timestamp: metadata.timestamp,
      camera_id: metadata.camera_id,
      size_bytes: metadata.size_bytes ?? null,
      capture_duration_ms: metadata.capture_duration_ms ?? null,
      jpeg_quality: metadata.jpeg_quality ?? null,
      lastUpdatedMinutes: calculateMinutesSince(metadata.timestamp),
    };

    // Store full metadata in sessionStorage for modal access
    const storageKey = secondary
      ? `10x_image_metadata_${deviceId}_secondary`
      : `10x_image_metadata_${deviceId}`;
    sessionStorage.setItem(storageKey, JSON.stringify(metadata));
  } else {
    metadataError = describeFetchError(metadataResult.reason, 'image metadata');
  }

  return { imageObjectUrl, displayFields, metadata, imageError, metadataError };
};
